import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { TableCell, TableRow } from '@/components/ui/table'
import { Call } from '@/types/calls'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import {
  Check,
  HelpCircle,
  Phone,
  Send,
  Triangle,
  Users,
  X
} from 'lucide-react'

interface CallRowProps {
  call: Call
  selected: boolean
  onSelect: () => void
  onReview: (reviewed: boolean) => void
  onDetails: () => void
}

export function CallRow({ call, selected, onSelect, onReview, onDetails }: CallRowProps) {
  // Handle checkbox change - mark as reviewed when checked
  const handleCheckboxChange = () => {
    onSelect();
    onReview(!selected); // Toggle reviewed status
  };
  // Format call time
  const formatCallTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime)
    const end = new Date(endTime)
    const dateStr = format(start, 'dd.MM.yyyy', { locale: de })
    const timeRange = `${format(start, 'HH:mm', { locale: de })} - ${format(end, 'HH:mm', { locale: de })}`
    return { dateStr, timeRange }
  }

  // Get result icons - up to 3 icons: main outcome + forward (optional) + SMS (optional)
  const getResultIcons = () => {
    const icons = []
    const actionTypes = call.actions.map(a => a.type)

    // 1. MAIN OUTCOME (always shown - bold in requirements)
    // Check for successful reservation
    const isSuccessfulReservation = actionTypes.some(type =>
      type === 'BOOKING_DONE' ||
      type === 'BOOKING_DONE_FORCONFIRMATION' ||
      type === 'BUSINESSDATA_DONE'
    )

    // Check for failed reservation attempt
    const isFailedReservation = actionTypes.some(type =>
      type === 'NO_TABLE_AVAILABLE' ||
      type === 'BOOKED_OUT' ||
      type === 'CLOSED_DAY' ||
      type === 'CLOSED_TIME' ||
      type === 'CLOSED_TIME_SUGGEST' ||
      type === 'SUGGEST_OTHERTIME_ALREADYCLOSED' ||
      type === 'SUGGEST_OTHERTIME_CURRENTLYCLOSED' ||
      type === 'SUGGEST_OTHERTIME_BOOKINGWINDOW_CLOSED' ||
      type === 'BOOKINGWINDOW_CLOSED_NO_SUGGESTIONS_ABSOLUTE_TIME' ||
      type === 'RESTAURANT_CLOSED_NOSUGGESTIONS' ||
      type === 'BOOKING_FAILURE_BOOKINGWINDOW_CLOSED'
    )

    // Show main outcome icon
    if (isSuccessfulReservation) {
      // Erfolgreiche Reservierung: grüner Haken
      icons.push(
        <div key="success" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50">
          <Check className="h-5 w-5 text-green-600 stroke-[2.5]" />
        </div>
      )
    } else if (isFailedReservation) {
      // Reservierungsanfrage nicht erfolgreich: rotes X
      icons.push(
        <div key="failure" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50">
          <X className="h-5 w-5 text-red-600 stroke-[2.5]" />
        </div>
      )
    } else {
      // Sonstige Anfrage: schwarzes Fragezeichen
      icons.push(
        <div key="other" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
          <HelpCircle className="h-5 w-5 text-gray-900 stroke-[2]" />
        </div>
      )
    }

    // 2. FORWARD STATUS (optional)
    // Check conversationForwardState from API data
    const forwardAction = call.actions.find(a => a.data?.conversationForwardState)
    const forwardState = forwardAction?.data?.conversationForwardState

    if (forwardState && forwardState !== 'NO_FORWARD') {
      if (forwardState === 'FORWARD_SUCCESSFUL' || forwardState === 'CALLFORWARD_ACTIVE') {
        // Weiterleitung erfolgreich: grüner Telefonhörer
        icons.push(
          <div key="forward-success" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50">
            <Phone className="h-5 w-5 text-green-600 stroke-[2]" />
          </div>
        )
      } else {
        // Weiterleitung nicht erfolgreich: roter Telefonhörer
        icons.push(
          <div key="forward-failed" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50">
            <Phone className="h-5 w-5 text-red-600 stroke-[2]" />
          </div>
        )
      }
    }

    // 3. SMS SENT (optional)
    const hasSms = actionTypes.some(type =>
      type.includes('SMS') ||
      type.includes('MESSAGE')
    )

    if (hasSms) {
      // SMS versendet: schwarzer Papierflieger
      icons.push(
        <div key="sms" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
          <Send className="h-5 w-5 text-gray-900 stroke-[2]" />
        </div>
      )
    }

    return icons
  }

  // Format caller info
  const callerName = call.callerName || 'N. A.'
  const callerPhone = call.callerPhone || 'Unknown'

  // Format booking time - zweizeilig
  const bookingTime = call.bookingTime
    ? {
      date: format(new Date(call.bookingTime), 'dd.MM.yyyy', { locale: de }),
      time: format(new Date(call.bookingTime), 'HH:mm', { locale: de })
    }
    : null

  // Format booking ID with delta icon
  const bookingIdDisplay = call.bookingId ? (
    <div className="flex items-center gap-2">
      <Triangle className="h-3.5 w-3.5 text-purple-500 fill-purple-500" />
      <span className="text-sm font-mono text-gray-900">{call.bookingId.substring(0, 18)}...</span>
    </div>
  ) : (
    <span className="text-gray-400 text-base">–</span>
  )

  const { dateStr, timeRange } = formatCallTime(call.startTime, call.endTime)

  return (
    <TableRow className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 h-20">
      {/* Selection checkbox */}
      <TableCell className="pl-6 w-12 py-4">
        <Checkbox
          checked={selected || call.reviewed}
          onCheckedChange={handleCheckboxChange}
          aria-label={`Mark call ${call.sessionId} as reviewed`}
        />
      </TableCell>

      {/* Call time */}
      <TableCell className="whitespace-nowrap">
        <div className="space-y-1">
          <div className="font-semibold text-gray-900 text-base">{dateStr}</div>
          <div className="text-sm text-gray-600">{timeRange}</div>
        </div>
      </TableCell>

      {/* Result icons */}
      <TableCell className="whitespace-nowrap">
        <div className="flex items-center gap-2">
          {getResultIcons()}
        </div>
      </TableCell>

      {/* Caller info */}
      <TableCell>
        <div className="space-y-1">
          <div className="font-semibold text-gray-900 text-base">{callerName}</div>
          <div className="text-sm text-gray-600">{callerPhone}</div>
        </div>
      </TableCell>

      {/* Booking time */}
      <TableCell className="whitespace-nowrap">
        {bookingTime ? (
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{bookingTime.date}</div>
            <div className="text-sm text-gray-600">{bookingTime.time}</div>
          </div>
        ) : (
          <span className="text-gray-400 text-base">–</span>
        )}
      </TableCell>

      {/* Guests */}
      <TableCell className="whitespace-nowrap">
        {call.guestCount ? (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-500" />
            <span className="font-semibold text-gray-900 text-base">{call.guestCount}</span>
          </div>
        ) : (
          <span className="text-gray-400 text-base">–</span>
        )}
      </TableCell>

      {/* Booking ID */}
      <TableCell className="min-w-[200px]">
        {bookingIdDisplay}
      </TableCell>

      {/* Details button */}
      <TableCell className="text-right pr-6 w-32">
        <Button
          size="sm"
          onClick={onDetails}
          className="bg-brand-primary hover:bg-brand-primary-dark text-white font-medium text-sm px-4 py-2 h-9 shadow-sm"
        >
          Details
        </Button>
      </TableCell>
    </TableRow>
  )
}
